import { AnnualFilter } from '@/common/components/AnnualFilter'
import Button from '@/common/components/Button';
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import Select from '@/common/components/Select'
import useCompany from '@/common/hooks/queries/useCompany'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { getFromLocalStorage, setInLocalStorage } from '@/common/services/localStorageGetSet';
import { Mapper } from '@/common/services/mapper'
import { CompanyBranchData } from '@/common/types/companyBranches.type'
import { Option } from '@/common/types/option'
import { CreateGovernmentAllocationModal } from '@/features/privateCompanies/components/CreateGovernmentAllocationModal'
import { useCompanyGovernmentAllocationsInfinite } from '@/features/privateCompanies/hooks/useCompanyGovernmentAllocationsInfinite'
import { CompanyGovernmentAllocationData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type'
import { format } from 'date-fns'
import { GanttChartIcon, Grid2X2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom'
import GovernmentAllocationTimelineView from './Views/Timeline';
import GovernmentAllocationTableView from './Views/Table';
import FilterCheckbox from '@/common/components/FilterCheckbox';
import { companyGovernmentAllocationStatusOptions } from '../Show/constants/governmentAllocationStatus.constant';
import LoadingSpinner from '@/common/components/LoadingSpinner';
import { useAvailableCompanyBranches } from '@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches';

const List = () => {
  const { id: companyId } = useParams()

  const { data: company } = useCompany({ id: companyId || '' })

  const companyPaymentModality = company?.current_contract?.payment_modality;

  const [view, setView] = useState<string>(getFromLocalStorage('companies_government_allocation_view') || 'timeline');

  const { filterParams, setFilterParams } = useFilterParams()

  const [searchBranch, setSearchBranch] = useDebounce('')

  const {
    data: companyGovernmentAllocations,
    isPending: companyGovernmentAllocationsIsPending,
    isFetching: companyGovernmentAllocationsIsFetching,
    hasNextPage: hasNextPageCompanyGovernmentAllocations,
    isFetchingNextPage: isFetchingNextPageCompanyGovernmentAllocations,
    fetchNextPage: fetchNextPageCompanyGovernmentAllocations,
    error: companyGovernmentAllocationsError = {} as any,
    refetch: refetchCompanyGovernmentAllocations,
  } = useCompanyGovernmentAllocationsInfinite<CompanyGovernmentAllocationData[]>({
    filters: {
      companyId: companyId || '',
      branchId: filterParams.branch ? filterParams.branch?.value : '',
      startDate: format(new Date(filterParams.year || new Date().getFullYear(), 11, 31), 'yyyy-MM-dd'),
      endDate: format(new Date(filterParams.year || new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
      showDescendants: filterParams.showDescendants || undefined,
      status: filterParams.status?.value,
    },
  })

  const { data: companyBranchOptions } = useAvailableCompanyBranches({
    companyId,
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  useEffect(() => {
    if (!filterParams.branch?.value) setFilterParams((prev) => ({ ...prev, showDescendants: null }))
  }, [filterParams.branch?.value]);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyGovernmentAllocationsError?.status}
      refetchFunction={refetchCompanyGovernmentAllocations}
    >
      <div className="flex flex-col gap-6">
        <div className="flex justify-between">
          <div className="flex items-center gap-x-2">
            <Button
              variant="outline-text"
              onClick={() => setView((prev) => {
                const newView = prev === 'timeline' ? 'table' : 'timeline';
                setInLocalStorage('companies_government_allocation_view', newView);
                return newView;
              })}
              className="-ml-4"
              disabled={companyGovernmentAllocationsIsPending || companyGovernmentAllocationsError?.status === 403}
            >
              {view === 'timeline' && <Grid2X2Icon className="w-8 h-8" strokeWidth={1} color="rgb(12 140 233)" />}
              {view === 'table' && <GanttChartIcon className="w-8 h-8" strokeWidth={1} color="rgb(12 140 233)" />}
            </Button>

            <AnnualFilter setFilterParams={setFilterParams} year={filterParams.year || new Date().getFullYear()} />

            <Select
              placeholder="Status"
              className="text-xs rounded-xl bg-white-100 shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)] z-20 min-w-40"
              badge
              value={filterParams.status?.value ? filterParams.status : null}
              onChange={(e) => setFilterParams((prev) => ({ ...prev, status: (e as Option).value ? e : undefined }))}
              options={[{ label: 'Todas', value: '' }, ...companyGovernmentAllocationStatusOptions]}
            />

            <Select
              placeholder="Filial"
              className="text-xs rounded-xl bg-white-100 shadow-[0px_0px_8px_4px_rgba(0,_0,_0,_0.2)] z-20 min-w-40"
              badge
              value={filterParams.branch?.value ? filterParams.branch : null}
              onChange={(e) => setFilterParams((prev) => ({ ...prev, branch: (e as Option).value ? e : undefined }))}
              onInputChange={setSearchBranch}
              options={[{ label: 'Todas', value: '' }, ...companyBranchOptions as Option[]]}
            />

            <FilterCheckbox
              id='showDescendants'
              name='showDescendants'
              label='Mostrar descendentes'
              isChecked={!!filterParams.showDescendants}
              onChange={(showDescendants) => setFilterParams((prev) => ({ ...prev, showDescendants: showDescendants ? true : null }))}
              isDisabled={!filterParams.branch?.value}
            />
          </div>

          <div className='flex items-center gap-2'>
            <LoadingSpinner isLoading={companyGovernmentAllocationsIsFetching} />
            <CreateGovernmentAllocationModal companyPaymentModality={companyPaymentModality} />
          </div>
        </div>

        {view === 'timeline' && (
          <GovernmentAllocationTimelineView
            companyGovernmentAllocations={companyGovernmentAllocations}
            companyGovernmentAllocationsIsPending={companyGovernmentAllocationsIsPending}
            companyPaymentModality={companyPaymentModality}
            hasNextPage={hasNextPageCompanyGovernmentAllocations}
            isFetchingNextPage={isFetchingNextPageCompanyGovernmentAllocations}
            fetchNextPage={fetchNextPageCompanyGovernmentAllocations}
          />
        )}

        {view === 'table' && (
          <GovernmentAllocationTableView
            companyGovernmentAllocations={companyGovernmentAllocations}
            companyGovernmentAllocationsIsPending={companyGovernmentAllocationsIsPending}
          />
        )}
      </div>
    </ErrorMessageWrapper>
  )
}

export default List
